import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/Dashboard.js (no status badges; client cards show outstanding
//     request count only)
//   - src/pages/ClientDetail.js (requests bucketed into three tabs based on
//     transaction counts: Outstanding, Completed, Cancelled. There is no
//     stored status field with values like Draft/Sent/Stalled.)
//   - src/pages/RequestDetail.js (Progress card with bar + Uploaded / Lost /
//     Pending counts; statusFilter dropdown All/Pending/Uploaded/Lost;
//     statusStyle returns one of Pending ⏳, Uploaded ✓, Lost ⚠)
//   - Sort by column-header clicks (SortTh component), not a separate
//     "sort by X" dropdown
// Removed:
//   - Fabricated status taxonomy "Draft / Sent / In progress / Complete /
//     Stalled". No such states exist. Requests are just Outstanding,
//     Completed, or Cancelled (each derived from transaction counts).
//   - "12 of 28" badge text. The dashboard shows an outstanding receipt
//     count per client, not a "X of Y" string per request.
//   - "Stalled" detection at 7+ days of inactivity. No such feature exists.
//   - Per-row icons described as "circle / green tick / grey document".
//     Real icons are ⏳ Pending / ✓ Uploaded / ⚠ Lost.
//   - "Filter to outstanding rows only" and "filter to lost-receipt
//     declarations only" as separate features. There's one Status dropdown:
//     All / Pending / Uploaded / Lost.
// Pass 2 added:
//   - The Progress card with progress bar and counts at top of request page
//   - Row-level features: client notes (💬) and bookkeeper internal notes (📝)
//   - Status-based row tinting (uploaded = faint green, lost = faint amber)
//   - Bulk action bar when transactions are selected

export default {
  title:   "Reading a request's progress at a glance",
  summary: 'How to tell what is outstanding, what is complete, and what needs your attention.',
  body: (
    <>
      <P>
        Receipt Chaser doesn't make you ask the client where they are.
        Every request shows what's been done at a glance, both at the
        client level and inside each individual request. This guide
        explains where to look.
      </P>

      <H>On the dashboard</H>
      <P>
        The dashboard lists your active clients with a small outstanding
        count beside each. The count is the number of receipts still
        needed across all of that client's open requests. If a client
        has nothing outstanding, no count appears.
      </P>
      <P>
        Two summary cards at the top show your total active client
        count and your total outstanding receipts across every client.
        Useful for a Monday-morning glance to see how much chasing is
        on your plate this week.
      </P>

      <Screenshot
        src="/support-screenshots/05 - dashboard.png"
        alt="The Your Clients dashboard with two summary cards (3 Active clients, 70 Outstanding receipts), a search box, and three client rows each with an outstanding-count badge and action buttons."
        caption="The dashboard with the two summary cards at the top and the client list below, each showing its outstanding count."
      />

      <H>On a client's page</H>
      <P>
        Click a client to see their requests, organised into three tabs:
      </P>
      <UL>
        <Li>
          <strong>Outstanding</strong>. Requests where at least one
          transaction is still pending. This is where active work
          lives.
        </Li>
        <Li>
          <strong>Completed</strong>. Every transaction in the request
          has either been uploaded or marked as lost. The request can
          be reviewed and downloaded.
        </Li>
        <Li>
          <strong>Cancelled</strong>. Tab only appears when there's
          at least one cancelled request. See{' '}
          <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>.
        </Li>
      </UL>

      <Screenshot
        src="/support-screenshots/05 - client tabs.png"
        alt="The top of a client detail page with the Outstanding tab selected showing 2 requests, plus Completed (1) and Cancelled (1) tabs. Each request row shows its title, transaction count, uploaded count, and Copy link / View buttons."
        caption="A client's detail page showing the Outstanding, Completed, and Cancelled tabs. The Cancelled tab only appears once at least one request has been cancelled."
      />

      <H>Inside a single request</H>
      <P>
        Open a request to see the full picture. At the top is a{' '}
        <strong>Progress card</strong> with a bar and three counts:
      </P>
      <UL>
        <Li><strong>✓ Uploaded</strong>. Receipts the client has provided.</Li>
        <Li><strong>⚠ Lost</strong>. Transactions the client has formally declared as a lost receipt (see <Link to="/support/handling-lost-receipts">Handling lost receipts</Link>).</Li>
        <Li><strong>⏳ Pending</strong>. Transactions still waiting.</Li>
      </UL>
      <P>
        Once everything is either uploaded or declared lost, the bar
        turns green and a <Code>⬇ Download all</Code> button appears
        next to the counts.
      </P>

      <Screenshot
        src="/support-screenshots/05 - request progress.png"
        alt="A Progress card with a teal progress bar partly filled, status counts on the right showing 2 uploaded, 1 lost, and 3 pending, plus a Download all button."
        caption="The Progress card at the top of a request, showing the bar and the three counts. The Download all button appears once the request is complete."
      />

      <H>The transaction table</H>
      <P>
        Below the progress card and meta cards (auto-reminder, notes
        for client) is the transaction table. Each row shows the
        status, date, description, type (income or expenditure), and
        amount.
      </P>
      <P>
        Rows are tinted by status so you can scan the table at a
        glance: faint green for uploaded, faint amber for lost,
        nothing for pending. Click any column header to sort by that
        column.
      </P>

      <Screenshot
        src="/support-screenshots/05 - request table.png"
        alt="The transaction table showing six rows: two uploaded (green tinted, with file chips), one lost (amber tinted, with the client's explanation inline), and three pending. Each row has a delete icon at the end. A Showing dropdown is set to All (6) above the table."
        caption="The transaction table on an open request. Uploaded rows have a green tint and a file chip; the lost row has an amber tint and the client's explanation inline; pending rows have no tint."
      />

      <H>Filtering and sorting</H>
      <P>
        Use the <Code>Showing</Code> dropdown above the table to
        narrow the view. You can pick <em>All</em>, <em>Pending</em>,
        {' '}<em>Uploaded</em>, or <em>Lost</em>. The count next to
        each option shows how many transactions fall into that bucket.
      </P>
      <P>
        Click any column header to sort by that column. Click again
        to reverse the order. Useful sort orders for chasing:
      </P>
      <UL>
        <Li>By Amount, descending. Chase the biggest items first.</Li>
        <Li>By Status, then by Date. Groups all pending rows together with the oldest at the top.</Li>
      </UL>

      <H>What gets attached to each row</H>
      <P>
        As the request progresses, individual rows accumulate more
        information you can see directly on the table:
      </P>
      <UL>
        <Li>
          <strong>Receipt thumbnails.</strong> Uploaded files appear
          below the description, as small thumbnails you can click
          to view or download.
        </Li>
        <Li>
          <strong>Client notes (💬).</strong> Anything the client
          typed when uploading. Useful when the receipt is unusual
          (e.g. "Personal portion £20, business £80").
        </Li>
        <Li>
          <strong>Lost receipt declarations.</strong> If a row is
          marked lost, the client's explanation appears in amber
          below the description. The "Wholly business" flag is also
          shown.
        </Li>
        <Li>
          <strong>Your internal notes (📝).</strong> Notes you've
          added for yourself or your team about a specific
          transaction. Not visible to the client.
        </Li>
      </UL>

      <H>Selecting and acting on rows in bulk</H>
      <P>
        Tick the checkbox on any row (or the master checkbox to
        select everything in the current filter) to reveal a bulk
        action bar at the top of the table. From there you can
        download just the selected rows' attachments as a zip, edit
        their descriptions in one go, or delete them.
      </P>

      <Screenshot
        src="/support-screenshots/05 - bulk actions.png"
        alt="The transaction table with two rows selected (highlighted in teal). A dark bulk-action bar above the table reads '6 transactions selected' with buttons for Download as zip, Edit descriptions, Delete selected, and Cancel."
        caption="Ticking row checkboxes reveals a bulk-action bar above the table with Download as zip, Edit descriptions, and Delete options."
      />

      <H>Notifications</H>
      <P>
        You don't have to keep checking the dashboard. Receipt Chaser
        emails you when a client finishes a request, so you know it's
        ready to review. The toggle is on{' '}
        <Code>Settings → Email template</Code>, on the same screen
        where you customise the client-facing template.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/reviewing-completed-receipts">Reviewing and downloading completed receipts</Link>
        </Li>
        <Li>
          <Link to="/support/handling-lost-receipts">Handling lost receipts</Link>
        </Li>
        <Li>
          <Link to="/support/resending-or-editing-a-request">Resending or editing a request</Link>
        </Li>
      </UL>
    </>
  ),
}
